import { Container, Sprite, Texture, Graphics, Text, TextStyle } from 'pixi.js';
import type { WeaponDefinition } from '../weapons/types';
import { isMobileDevice } from './mobileDetect';
import { COLORS, drawPanel } from './theme';

export interface WeaponSlot {
  weapon: WeaponDefinition;
  ammo: number;
  texture: Texture;
  textureSelected: Texture;
}

export interface WeaponPanelConfig {
  weapons: WeaponSlot[];
  onSelect: (weapon: WeaponDefinition) => void;
}

const IS_MOBILE = isMobileDevice();
const SLOT_SIZE = IS_MOBILE ? 52 : 40;
const SLOT_PADDING = IS_MOBILE ? 5 : 4;
const COLS = IS_MOBILE ? 5 : 5;
const PANEL_PADDING = IS_MOBILE ? 10 : 8;

const ALPHA_WEAPONS = new Set([
  'molotov',
  'napalmStrike',
  'bungee',
  'rope',
  'homing',
  'jetpack',
  'supersheep',
]);

export class WeaponPanel {
  container: Container;
  private slots: Container[] = [];
  private slotData: WeaponSlot[] = [];
  private selectedIndex = -1;
  private visible = false;
  private onSelect: (weapon: WeaponDefinition) => void;
  private background!: Graphics;

  constructor(config: WeaponPanelConfig) {
    this.container = new Container();
    this.container.visible = false;
    this.onSelect = config.onSelect;

    const stableWeapons = config.weapons.filter((w) => !ALPHA_WEAPONS.has(w.weapon.id));
    const alphaWeapons = config.weapons.filter((w) => ALPHA_WEAPONS.has(w.weapon.id));
    this.slotData = [...stableWeapons, ...alphaWeapons];

    this.createBackground(stableWeapons.length, alphaWeapons.length);
    this.createSlots(stableWeapons.length);
  }

  private createBackground(stableCount: number, alphaCount: number): void {
    const stableRows = Math.ceil(stableCount / COLS);
    const alphaRows = Math.ceil(alphaCount / COLS);
    const totalRows = stableRows + (alphaCount > 0 ? alphaRows + 0.6 : 0);

    const width = COLS * (SLOT_SIZE + SLOT_PADDING) + PANEL_PADDING * 2 - SLOT_PADDING;
    const height = totalRows * (SLOT_SIZE + SLOT_PADDING) + PANEL_PADDING * 2 - SLOT_PADDING;

    this.background = new Graphics();
    drawPanel(this.background, 0, 0, width, height, { radius: 10, alpha: 0.94 });
    this.container.addChild(this.background);
  }

  private createSlots(stableCount: number): void {
    const stableRows = Math.ceil(stableCount / COLS);
    const alphaCount = this.slotData.length - stableCount;

    if (alphaCount > 0) {
      const alphaLabelStyle = new TextStyle({
        fontFamily: 'Arial',
        fontSize: 10,
        fill: 0xff6666,
        fontWeight: 'bold',
      });
      const alphaLabel = new Text({ text: '— ALPHA —', style: alphaLabelStyle });
      alphaLabel.position.set(
        PANEL_PADDING,
        PANEL_PADDING + stableRows * (SLOT_SIZE + SLOT_PADDING) + 3,
      );
      this.container.addChild(alphaLabel);
    }

    for (let i = 0; i < this.slotData.length; i++) {
      const slot = this.slotData[i];
      const isAlpha = i >= stableCount;

      let col: number;
      let row: number;

      if (isAlpha) {
        const alphaIndex = i - stableCount;
        col = alphaIndex % COLS;
        row = stableRows + Math.floor(alphaIndex / COLS);
      } else {
        col = i % COLS;
        row = Math.floor(i / COLS);
      }

      const slotContainer = new Container();

      let yOffset = PANEL_PADDING + row * (SLOT_SIZE + SLOT_PADDING);
      if (isAlpha) {
        yOffset += 20;
      }

      slotContainer.position.set(PANEL_PADDING + col * (SLOT_SIZE + SLOT_PADDING), yOffset);

      const bg = new Graphics();
      this.paintSlot(bg, isAlpha, 'idle');
      slotContainer.addChild(bg);

      const icon = new Sprite(slot.texture);
      icon.anchor.set(0.5);
      icon.position.set(SLOT_SIZE / 2, SLOT_SIZE / 2);
      const scale = Math.min((SLOT_SIZE - 8) / icon.width, (SLOT_SIZE - 8) / icon.height);
      icon.scale.set(scale);
      slotContainer.addChild(icon);

      if (slot.ammo >= 0 && slot.ammo < 99) {
        const ammoStyle = new TextStyle({
          fontFamily: 'Arial',
          fontSize: 10,
          fontWeight: 'bold',
          fill: slot.ammo > 0 ? 0xffffff : 0xff4444,
        });
        const ammoText = new Text({ text: slot.ammo.toString(), style: ammoStyle });
        ammoText.anchor.set(1, 1);
        ammoText.position.set(SLOT_SIZE - 2, SLOT_SIZE - 2);
        slotContainer.addChild(ammoText);
      }

      slotContainer.eventMode = 'static';
      slotContainer.cursor = 'pointer';
      slotContainer.on('pointerdown', () => this.selectSlot(i));
      slotContainer.on('pointerover', () => this.highlightSlot(i, true));
      slotContainer.on('pointerout', () => this.highlightSlot(i, false));

      this.container.addChild(slotContainer);
      this.slots.push(slotContainer);
    }
  }

  private highlightSlot(index: number, highlight: boolean): void {
    const slot = this.slots[index];
    if (!slot) return;

    const isAlpha = ALPHA_WEAPONS.has(this.slotData[index]?.weapon.id || '');
    const bg = slot.children[0] as Graphics;
    const state = index === this.selectedIndex ? 'selected' : highlight ? 'hover' : 'idle';
    this.paintSlot(bg, isAlpha, state);
  }

  /** Beveled slot tile in idle / hover / selected states (Fusion chrome). */
  private paintSlot(bg: Graphics, isAlpha: boolean, state: 'idle' | 'hover' | 'selected'): void {
    bg.clear();
    const base = isAlpha ? 0x3a2336 : 0x16294a;
    const fill =
      state === 'selected'
        ? isAlpha
          ? 0x6a3a1c
          : 0x2f5a2f
        : state === 'hover'
          ? isAlpha
            ? 0x4a2f44
            : 0x1f3a66
          : base;
    const stroke =
      state === 'selected'
        ? COLORS.gold
        : state === 'hover'
          ? COLORS.cyan
          : isAlpha
            ? COLORS.purple
            : COLORS.stroke;
    bg.roundRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6).fill({ color: fill });
    bg.roundRect(2, 2, SLOT_SIZE - 4, SLOT_SIZE * 0.4, 5).fill({ color: COLORS.white, alpha: 0.1 });
    bg.roundRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6).stroke({
      color: stroke,
      width: state === 'idle' ? 1.5 : 2,
    });
  }

  private selectSlot(index: number): void {
    const data = this.slotData[index];
    if (!data || data.ammo === 0) return;

    const prevSelected = this.selectedIndex;
    this.selectedIndex = index;

    if (prevSelected >= 0) {
      this.highlightSlot(prevSelected, false);
    }
    this.highlightSlot(index, false);

    this.onSelect(data.weapon);
    this.hide();
  }

  show(x: number, y: number): void {
    this.container.position.set(x, y);
    this.container.visible = true;
    this.visible = true;
  }

  hide(): void {
    this.container.visible = false;
    this.visible = false;
  }

  toggle(x: number, y: number): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show(x, y);
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  setSelectedWeapon(weaponId: string): void {
    const prevSelected = this.selectedIndex;
    this.selectedIndex = this.slotData.findIndex((s) => s.weapon.id === weaponId);

    if (prevSelected >= 0) {
      this.highlightSlot(prevSelected, false);
    }
    if (this.selectedIndex >= 0) {
      this.highlightSlot(this.selectedIndex, false);
    }
  }

  /** Rebuild the panel for a new weapon set (e.g. a different tank type whose
   *  turn just started). Preserves nothing - the caller re-applies the
   *  selected weapon afterwards. */
  setWeapons(weapons: WeaponSlot[]): void {
    this.container.removeChildren();
    this.slots = [];
    this.selectedIndex = -1;
    const stableWeapons = weapons.filter((w) => !ALPHA_WEAPONS.has(w.weapon.id));
    const alphaWeapons = weapons.filter((w) => ALPHA_WEAPONS.has(w.weapon.id));
    this.slotData = [...stableWeapons, ...alphaWeapons];
    this.createBackground(stableWeapons.length, alphaWeapons.length);
    this.createSlots(stableWeapons.length);
  }

  updateAmmo(weaponId: string, ammo: number): void {
    const index = this.slotData.findIndex((s) => s.weapon.id === weaponId);
    if (index >= 0) {
      this.slotData[index].ammo = ammo;
      this.updateSlotAmmoDisplay(index);
    }
  }

  private updateSlotAmmoDisplay(index: number): void {
    const slot = this.slots[index];
    const data = this.slotData[index];
    if (!slot || !data) return;

    const existingText = slot.children.find((c) => c instanceof Text) as Text | undefined;
    if (existingText) {
      existingText.text = data.ammo.toString();
      existingText.style.fill = data.ammo > 0 ? 0xffffff : 0xff4444;
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
